import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import NavMenu from "./navigation/NavMenu";
import DocumentTitle from "./navigation/DocumentTitle";
import {
  SIDEBAR_COLLAPSED_STORAGE_KEY,
  SIDEBAR_TRANSITION_MS,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
} from "./navigation/navConstants";
import { isEmpty } from "../services/Helpers";

export function Layout(props) {
  const theme = useTheme();
  // noSsr so the sidebar renders at its real size on first paint instead of flashing
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const user = useSelector((state) => state.user?.user);

  const isAuthenticated = !isEmpty(user);

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true"
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const sidebarOffset =
    isAuthenticated && isDesktop
      ? collapsed
        ? SIDEBAR_WIDTH_COLLAPSED
        : SIDEBAR_WIDTH
      : 0;

  // Published so viewport-anchored elements (sticky action bars, overlays) can
  // start where the sidebar ends instead of hiding underneath it.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-sidebar-offset",
      `${sidebarOffset}px`
    );
  }, [sidebarOffset]);

  // Tabulator and FullCalendar size themselves to their container and only
  // recalculate on window resize, so nudge them once the width animation ends.
  useEffect(() => {
    const timer = setTimeout(
      () => window.dispatchEvent(new Event("resize")),
      SIDEBAR_TRANSITION_MS + 50
    );
    return () => clearTimeout(timer);
  }, [sidebarOffset]);

  const content = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DocumentTitle />
      {props.children}
    </LocalizationProvider>
  );

  // Signed out the only views are the auth screens, which bring their own chrome.
  if (!isAuthenticated) {
    return (
      <Box className="app-shell app-shell--public">
        <div className="public-container">{content}</div>
      </Box>
    );
  }

  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <Box className="app-shell">
      <NavMenu
        isDesktop={isDesktop}
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCollapseToggle={() => setCollapsed((value) => !value)}
        onMobileToggle={() => setMobileOpen((value) => !value)}
        onMobileClose={() => setMobileOpen(false)}
      />
      <Box
        component="main"
        className="app-main"
        sx={{
          width: { xs: "100%", md: `calc(100% - ${sidebarWidth}px)` },
          ml: { xs: 0, md: `${sidebarWidth}px` },
          transition: `width ${SIDEBAR_TRANSITION_MS}ms ease, margin-left ${SIDEBAR_TRANSITION_MS}ms ease`,
        }}
      >
        <div className="root-container">{content}</div>
      </Box>
    </Box>
  );
}
